import { create } from 'zustand';

interface MoClientInfo {
  clientHash: string;
  name: string;
  moFamilyName: string;
  isActive: boolean;
}

interface PlayerStatus {
  state: string;
  current_video: string | null;
  position_secs: number;
  volume: number;
  playlist: string[];
  /** 화면이 실제로 꺼져 있는가 — 렌더러가 커널 CRTC ACTIVE 를 읽어 보고한 관측값.
   *  명령이 전달됐는지가 아니라 결과다. 원격지 화면은 눈으로 못 보므로 이게 유일한 근거. */
  screen_off?: boolean;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected';

interface RecentCommand {
  command: string;
  delivery: string;
  timestamp: number;
}

interface OverlayToggles {
  show_status: boolean;
  show_debug: boolean;
  show_info: boolean;
  hwaccel_active: boolean;
  play_log_enabled: boolean;
  qr_visible: boolean;
  /** 화면이 실제로 꺼져 있는가 — 렌더러가 커널 CRTC ACTIVE 를 읽어 보고한 관측값.
   *  명령 전달 여부가 아니라 결과다. 원격지 화면은 눈으로 못 보므로 이 값이 유일한 근거. */
  screen_off?: boolean;
}

interface MoControllerState {
  clients: MoClientInfo[];
  selectedClientHash: string | null;
  playerStatus: PlayerStatus | null;
  playerConnected: boolean;
  connectionState: ConnectionState;
  recentCommands: RecentCommand[];
  overlayToggles: OverlayToggles;
  detailedStatus: any | null;
  managerStatus: any | null;
  componentVersions: Record<string, string> | null;

  // internal
  _ws: WebSocket | null;
  _pollingTimer: ReturnType<typeof setInterval> | null;
  _startPolling: (hash: string) => void;

  loadClients: () => Promise<void>;
  selectClient: (hash: string | null) => void;
  connectMonitorWs: (hash: string) => void;
  disconnectMonitorWs: () => void;
  sendCommand: (command: string, params?: Record<string, any>) => Promise<void>;
  fetchPlayerStatus: (hash: string) => Promise<void>;
  clearCache: () => Promise<{ success: boolean; removed?: number; error?: string }>;
  removeFile: (fileHash: string) => Promise<{ success: boolean; removed?: boolean; error?: string }>;
  resetNetwork: () => Promise<{ success: boolean; removed_credentials?: number; error?: string }>;
  factoryReset: () => Promise<{
    success: boolean;
    forwarded?: boolean;
    moClientsAffected?: number;
    moClientFamiliesAffected?: number;
    error?: string;
  }>;
}

// Module-level variable — not in Zustand to avoid re-render notifications
let lastToggleAt = 0;

/** Shallow-compare two values by JSON serialization. Cheap for small objects like playerStatus. */
function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  return JSON.stringify(a) === JSON.stringify(b);
}

const TOGGLE_COMMAND_MAP: Record<string, keyof OverlayToggles> = {
  ToggleStatus: 'show_status',
  ToggleDebug: 'show_debug',
  ToggleInfo: 'show_info',
  ToggleHwaccel: 'hwaccel_active',
  TogglePlayLog: 'play_log_enabled',
  ToggleQr: 'qr_visible',
};

/** Extract toggle updates from a status object, suppressing server echo within 3s of local toggle. */
function extractToggleUpdates(statusObj: any, playLogEnabled?: boolean): Partial<OverlayToggles> {
  const recentToggle = Date.now() - lastToggleAt < 3000;
  if (recentToggle) return {};
  const updates: Partial<OverlayToggles> = {};
  if (statusObj) {
    if (statusObj.hwaccel_active !== undefined) updates.hwaccel_active = statusObj.hwaccel_active;
    if (statusObj.show_status !== undefined) updates.show_status = statusObj.show_status;
    if (statusObj.show_debug !== undefined) updates.show_debug = statusObj.show_debug;
    if (statusObj.show_info !== undefined) updates.show_info = statusObj.show_info;
    if (statusObj.qr_visible !== undefined) updates.qr_visible = statusObj.qr_visible;
    if (statusObj.screen_off !== undefined) updates.screen_off = statusObj.screen_off;
  }
  if (playLogEnabled !== undefined) updates.play_log_enabled = playLogEnabled;
  return updates;
}

export const useMoControllerStore = create<MoControllerState>((set, get) => ({
  clients: [],
  selectedClientHash: null,
  playerStatus: null,
  playerConnected: false,
  connectionState: 'disconnected',
  recentCommands: [],
  overlayToggles: { show_status: false, show_debug: false, show_info: false, hwaccel_active: false, play_log_enabled: false, qr_visible: false },
  detailedStatus: null,
  managerStatus: null,
  componentVersions: null,
  _ws: null,
  _pollingTimer: null,

  loadClients: async () => {
    try {
      const response = await fetch('/moclient-test/list', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const clients: MoClientInfo[] = (data.data || []).map((c: any) => ({
          clientHash: c.clientHash,
          name: c.name,
          moFamilyName: c.moFamilyName,
          isActive: c.isActive,
        }));
        set({ clients });
      }
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  },

  selectClient: (hash) => {
    const { disconnectMonitorWs, connectMonitorWs, fetchPlayerStatus } = get();
    disconnectMonitorWs();
    set({ selectedClientHash: hash, playerStatus: null, playerConnected: false, recentCommands: [], overlayToggles: { show_status: false, show_debug: false, show_info: false, hwaccel_active: false, play_log_enabled: false, qr_visible: false }, detailedStatus: null, managerStatus: null, componentVersions: null });
    if (hash) {
      connectMonitorWs(hash);
      fetchPlayerStatus(hash);
    }
  },

  connectMonitorWs: (hash) => {
    const { _ws } = get();
    if (_ws) {
      _ws.close();
    }

    set({ connectionState: 'connecting', _ws: null });

    try {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host;
      const ws = new WebSocket(`${wsProtocol}//${wsHost}/ws/monitor?clientHash=${encodeURIComponent(hash)}`);

      ws.onopen = () => {
        set({ _ws: ws });
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'player_status') {
            const status = msg.status;
            const toggleUpdates = extractToggleUpdates(status, msg.play_log_enabled);
            const state = get();
            const updates: Partial<MoControllerState> = {
              connectionState: 'connected',
            };
            const newPlayerConnected = msg.player_connected ?? (status != null);
            if (newPlayerConnected !== state.playerConnected) {
              updates.playerConnected = newPlayerConnected;
            }
            if (!jsonEqual(status, state.playerStatus)) {
              updates.playerStatus = status;
            }
            if (msg.manager_status != null && !jsonEqual(msg.manager_status, state.managerStatus)) {
              updates.managerStatus = msg.manager_status;
            }
            if (Object.keys(toggleUpdates).length > 0) {
              updates.overlayToggles = { ...state.overlayToggles, ...toggleUpdates };
            }
            if (Object.keys(updates).length > 1 || state.connectionState !== 'connected') {
              set(updates);
            }
          } else if (msg.type === 'detailed_status') {
            const ps = msg.player_status;
            const toggleUpdates = extractToggleUpdates(ps, msg.play_log_enabled);
            const state = get();
            const updates: Partial<MoControllerState> = {
              connectionState: 'connected',
            };
            const newPlayerConnected = msg.player_connected ?? state.playerConnected;
            if (newPlayerConnected !== state.playerConnected) {
              updates.playerConnected = newPlayerConnected;
            }
            const newPs = ps ?? state.playerStatus;
            if (!jsonEqual(newPs, state.playerStatus)) {
              updates.playerStatus = newPs;
            }
            if (!jsonEqual(msg.manager_status, state.managerStatus) && msg.manager_status != null) {
              updates.managerStatus = msg.manager_status;
            }
            // Compare downloads/peers/storage from detailedStatus to avoid re-render on unchanged data
            const prevDs = state.detailedStatus;
            if (!prevDs
              || !jsonEqual(msg.downloads, prevDs.downloads)
              || !jsonEqual(msg.peers, prevDs.peers)
              || !jsonEqual(msg.storage, prevDs.storage)
              || !jsonEqual(msg.tickets, prevDs.tickets)
              || (msg.player_connected !== prevDs.player_connected)
            ) {
              updates.detailedStatus = msg;
            }
            if (Object.keys(toggleUpdates).length > 0) {
              updates.overlayToggles = { ...state.overlayToggles, ...toggleUpdates };
            }
            // Only call set() if there are actual changes beyond connectionState
            if (Object.keys(updates).length > 1 || state.connectionState !== 'connected') {
              set(updates);
            }
          } else if (msg.type === 'component_versions') {
            set({ componentVersions: msg.versions });
          } else if (msg.type === 'moclient_connected') {
            set({ connectionState: 'connected' });
          } else if (msg.type === 'moclient_disconnected') {
            set({ connectionState: 'disconnected' });
            get()._startPolling(hash);
          }
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        const state = get();
        if (state._ws === ws) {
          set({ _ws: null });
          if (state.selectedClientHash === hash) {
            set({ connectionState: 'disconnected' });
            get()._startPolling(hash);
          }
        }
      };

      ws.onerror = () => {
        // onclose will fire after onerror
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      set({ connectionState: 'disconnected' });
      get()._startPolling(hash);
    }
  },

  disconnectMonitorWs: () => {
    const { _ws, _pollingTimer } = get();
    if (_ws) {
      _ws.close();
      set({ _ws: null });
    }
    if (_pollingTimer) {
      clearInterval(_pollingTimer);
      set({ _pollingTimer: null });
    }
    set({ connectionState: 'disconnected' });
  },

  sendCommand: async (command, params) => {
    const { selectedClientHash } = get();
    if (!selectedClientHash) return;

    try {
      const isToggle = command.startsWith('Toggle');
      if (isToggle) lastToggleAt = Date.now();

      const body: Record<string, any> = { command };
      if (params) body.params = params;

      const response = await fetch(`/moclient-test/player-command/${encodeURIComponent(selectedClientHash)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();
      const delivery = data.success ? (data.data?.delivery || 'unknown') : 'failed';

      // Update overlay toggle states from response (always apply — this is the command initiator)
      const respData = data.data?.response_data;
      const toggleUpdates: Partial<OverlayToggles> = {};
      if (respData) {
        if (respData.hwaccel_active !== undefined) toggleUpdates.hwaccel_active = respData.hwaccel_active;
        if (respData.show_status !== undefined) toggleUpdates.show_status = respData.show_status;
        if (respData.show_debug !== undefined) toggleUpdates.show_debug = respData.show_debug;
        if (respData.show_info !== undefined) toggleUpdates.show_info = respData.show_info;
        if (respData.play_log_enabled !== undefined) toggleUpdates.play_log_enabled = respData.play_log_enabled;
        if (respData.qr_visible !== undefined) toggleUpdates.qr_visible = respData.qr_visible;
      }
      // Optimistic toggle if delivery succeeded but no response_data yet
      if (delivery === 'channel' || delivery === 'realtime') {
        const toggleKey = TOGGLE_COMMAND_MAP[command];
        if (toggleKey && toggleUpdates[toggleKey] === undefined) {
          toggleUpdates[toggleKey] = !get().overlayToggles[toggleKey];
        }
      }

      set((state) => ({
        recentCommands: [
          { command, delivery, timestamp: Date.now() },
          ...state.recentCommands.slice(0, 9),
        ],
        ...(Object.keys(toggleUpdates).length > 0
          ? { overlayToggles: { ...state.overlayToggles, ...toggleUpdates } }
          : {}),
      }));
    } catch (error) {
      set((state) => ({
        recentCommands: [
          { command, delivery: 'failed', timestamp: Date.now() },
          ...state.recentCommands.slice(0, 9),
        ],
      }));
    }
  },

  clearCache: async () => {
    const { selectedClientHash } = get();
    if (!selectedClientHash) return { success: false, error: '선택된 디바이스 없음' };
    try {
      const response = await fetch(
        `/moclient-test/clear-cache/${encodeURIComponent(selectedClientHash)}`,
        { method: 'POST', credentials: 'include' },
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        const err = data.error || data.data?.error || `HTTP ${response.status}`;
        return { success: false, error: err };
      }
      return { success: true, removed: data.data?.removed };
    } catch (e: any) {
      return { success: false, error: e?.message || String(e) };
    }
  },

  resetNetwork: async () => {
    const { selectedClientHash } = get();
    if (!selectedClientHash) return { success: false, error: '선택된 디바이스 없음' };
    try {
      const response = await fetch(
        `/moclient-test/reset-network/${encodeURIComponent(selectedClientHash)}`,
        { method: 'POST', credentials: 'include' },
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        // 서버 route 는 두 가지 형태로 에러 보냄:
        //   - 503/500: { error: "..." } (top-level)
        //   - 200 + success=false: { success: false, data: { error: "..." } }
        const err = data.error || data.data?.error || `HTTP ${response.status}`;
        return { success: false, error: err };
      }
      return { success: true, removed_credentials: data.data?.removed_credentials };
    } catch (e: any) {
      return { success: false, error: e?.message || String(e) };
    }
  },

  factoryReset: async () => {
    const { selectedClientHash } = get();
    if (!selectedClientHash) return { success: false, error: '선택된 디바이스 없음' };
    try {
      const response = await fetch(
        `/moclient-test/factory-reset/${encodeURIComponent(selectedClientHash)}`,
        { method: 'POST', credentials: 'include' },
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        const err = data.error || data.data?.error || `HTTP ${response.status}`;
        return { success: false, error: err };
      }
      return {
        success: true,
        forwarded: data.data?.forwarded,
        moClientsAffected: data.data?.moClientsAffected,
        moClientFamiliesAffected: data.data?.moClientFamiliesAffected,
      };
    } catch (e: any) {
      return { success: false, error: e?.message || String(e) };
    }
  },

  removeFile: async (fileHash: string) => {
    const { selectedClientHash } = get();
    if (!selectedClientHash) return { success: false, error: '선택된 디바이스 없음' };
    try {
      const response = await fetch(
        `/moclient-test/remove-file/${encodeURIComponent(selectedClientHash)}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_hash: fileHash }),
        },
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        const err = data.error || data.data?.error || `HTTP ${response.status}`;
        return { success: false, error: err };
      }
      return { success: true, removed: data.data?.removed };
    } catch (e: any) {
      return { success: false, error: e?.message || String(e) };
    }
  },

  fetchPlayerStatus: async (hash) => {
    try {
      const response = await fetch(`/moclient-test/player-status/${encodeURIComponent(hash)}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.data?.playerStatus) {
          set({ playerStatus: data.data.playerStatus });
        }
      }
    } catch (error) {
      // ignore
    }
  },

  // Internal: start polling fallback
  _startPolling: (hash: string) => {
    const { _pollingTimer } = get();
    if (_pollingTimer) return; // already polling

    const timer = setInterval(() => {
      const state = get();
      if (state.selectedClientHash !== hash || state._ws) {
        clearInterval(timer);
        if (state._pollingTimer === timer) {
          set({ _pollingTimer: null });
        }
        return;
      }
      state.fetchPlayerStatus(hash);
    }, 5000);

    set({ _pollingTimer: timer });
  },
} as MoControllerState & { _startPolling: (hash: string) => void }));
